import { deriveWorkflowExpressionEdges } from "../expressions";
import { ZipTieSchema, type ZipTie, type ZipTieWire } from "../types";

/**
 * Zip ties are decoration, so a garbled entry or a reference to a deleted wire
 * must never fail a save or make a workflow unopenable. These helpers are
 * shared by the editor and the persistence layer so both sides normalise
 * identically.
 *
 * A wire is the sockets it joins. Edge row ids are not part of the identity:
 * reconnecting the same sockets keeps the strand, and a socket expression that
 * draws that connection is the same strand.
 */

export function zipTieWireKey(wire: ZipTieWire): string {
  return `${wire.sourceStepId}:${wire.sourceSocketId}>${wire.targetStepId}:${wire.targetSocketId}`;
}

function sameWires(
  left: readonly ZipTieWire[],
  right: readonly ZipTieWire[],
): boolean {
  return (
    left.length === right.length &&
    left.every((wire, index) => {
      const other = right[index];
      return (
        other !== undefined && zipTieWireKey(wire) === zipTieWireKey(other)
      );
    })
  );
}

export function uniqueWires(wires: readonly ZipTieWire[]): ZipTieWire[] {
  const seen = new Set<string>();
  const unique: ZipTieWire[] = [];
  for (const wire of wires) {
    const key = zipTieWireKey(wire);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(wire);
  }
  return unique;
}

/**
 * Drop members that no longer exist, then drop ties with nothing left to hold.
 * `validWireKeys` comes from `collectZipTieWireKeys`.
 */
export function pruneZipTies(
  ties: readonly ZipTie[],
  validWireKeys: Iterable<string>,
): ZipTie[] {
  const valid = new Set(validWireKeys);
  const kept: ZipTie[] = [];
  for (const tie of ties) {
    const wires = uniqueWires(
      tie.wires.filter((wire) => valid.has(zipTieWireKey(wire))),
    );
    if (wires.length === 0) continue;
    kept.push(sameWires(wires, tie.wires) ? tie : { ...tie, wires });
  }
  return kept;
}

/**
 * Rewrite step ids inside a tie. Cloning a version gives every step a fresh
 * id; the sockets stay. Unmapped ids are kept, so a dangling reference stays
 * visible instead of disappearing.
 */
export function remapZipTieStepIds(
  ties: readonly ZipTie[],
  stepIdMap: ReadonlyMap<string, string>,
): ZipTie[] {
  const nextId = (id: string): string => stepIdMap.get(id) ?? id;
  const kept: ZipTie[] = [];
  for (const tie of ties) {
    const wires = uniqueWires(
      tie.wires.map((wire) => ({
        ...wire,
        sourceStepId: nextId(wire.sourceStepId),
        targetStepId: nextId(wire.targetStepId),
      })),
    );
    kept.push(sameWires(wires, tie.wires) ? tie : { ...tie, wires });
  }
  return kept;
}

export type ZipTieExpressionStep = Parameters<
  typeof deriveWorkflowExpressionEdges
>[0][number];

/**
 * Every connection a tie is allowed to name: stored edges, plus wires a socket
 * expression draws. The two name the same sockets, so a tie made on one still
 * matches the other.
 */
export function collectZipTieWireKeys(
  edges: readonly ZipTieWire[],
  steps: readonly ZipTieExpressionStep[],
): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  const add = (wire: ZipTieWire): void => {
    if (!wire.sourceSocketId || !wire.targetSocketId) return;
    const key = zipTieWireKey(wire);
    if (seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  };
  for (const edge of edges) add(edge);
  for (const derived of deriveWorkflowExpressionEdges(steps)) add(derived);
  return keys;
}

/**
 * Coerce an untrusted value (a JSONB column, an imported file) into a valid
 * tie list, discarding entries that don't parse instead of throwing.
 */
export function parseZipTies(value: unknown): ZipTie[] {
  if (!Array.isArray(value)) return [];
  const ties: ZipTie[] = [];
  for (const entry of value) {
    const parsed = ZipTieSchema.safeParse(entry);
    if (parsed.success) ties.push(parsed.data);
  }
  return ties;
}
